import { FormAlert, useForm, useFormAlert } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Table,
} from 'antd';
import { FC, Fragment } from 'react';
import FRMVisitReportSalesMethods from './VisitReportOfficePage.MarketingAspect.Method';
import FRMVisitReportPaymentSystem from './VisitReportOfficePage.MarketingAspect.Payment';
import { MarketingAspect } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { submitFRMOfficeObservationMarketingAspect } from '@/libs/fleet-api/services/visit-reports';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import FRMVisitReportCompetitors from './VisitReportOfficePage.MarketingAspect.Competitor';
import FRMVisitReportBouwheer from './VisitReportOfficePage.MarketingAspect.Bouwheer';
import FleetTitle from '@/libs/antd/components/Title';

const FRMVisitReportMarketingAspect: FC<{ onSave: () => void }> = ({
	onSave = () => false,
}) => {
	const { form, value, fieldName } = useForm<MarketingAspect>();
	const { officeObservation, approvalHistories } =
		useFRMVisitReportPageContext();
	const { marketingAspect } = officeObservation ?? {};
	const submitFRMOfficeObservationMarketingAspectMutation = useMutation({
		mutationFn: submitFRMOfficeObservationMarketingAspect,
	});

	const { alerts, validate } = useFormAlert(form, null);

	return (
		<Form.Provider
			onFormFinish={(name, { values }) => {
				if (!name) return;

				submitFRMOfficeObservationMarketingAspectMutation.mutateAsync({
					...(values as MarketingAspect),
					surveyId: marketingAspect!.surveyId,
				});
				onSave();
			}}
		>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="productOffer"
				initialValues={
					{
						productOffer: marketingAspect?.productOffer,
					} as MarketingAspect
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('productOffer')}
							label="Produk/Jasa yang dipasarkan"
							rules={[rule.required('Produk/Jasa yang dipasarkan wajib diisi')]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />

			<FRMVisitReportSalesMethods />
			<FRMVisitReportPaymentSystem />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="isOtherBusiness"
				initialValues={
					{
						isOtherBusiness: marketingAspect?.isOtherBusiness,
						otherBusiness: marketingAspect?.otherBusiness,
						otherBusinessPortion: marketingAspect?.otherBusinessPortion,
					} as MarketingAspect
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('isOtherBusiness')}
							label="Apakah terdapat usaha lain?"
							rules={[rule.required('Apakah terdapat usaha lain wajib diisi')]}
						>
							<Radio.Group style={{ marginTop: 10 }}>
								<Radio value={true}>Ya</Radio>
								<Radio value={false}>Tidak</Radio>
							</Radio.Group>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				{value?.isOtherBusiness && (
					<Fragment>
						<Row gutter={[20, 40]}>
							<Col span={12}>
								<Form.Item
									name={fieldName('otherBusiness')}
									label="Sebutkan Usaha Lain"
									rules={[rule.required('Sebutkan Usaha Lain wajib diisi')]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									name={fieldName('otherBusinessPortion')}
									label="Porsi"
									rules={[rule.required('Porsi wajib diisi')]}
								>
									<InputNumber suffix="%" />
								</Form.Item>
							</Col>
						</Row>
						<Divider />
					</Fragment>
				)}
			</Form>

			<FRMVisitReportCompetitors />
			<FRMVisitReportBouwheer />

			{approvalHistories?.length ? (
				<Fragment>
					<FleetTitle>Approval History</FleetTitle>
					<Table
						dataSource={
							approvalHistories?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								width: 1,
								align: 'center',
								dataIndex: 'no',
							},
							{
								title: 'Name',
								dataIndex: 'approvalBy',
							},
							{
								title: 'Date',
								dataIndex: 'approvalDate',
							},
							{
								title: 'Process',
								dataIndex: 'approvalProcess',
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								dataIndex: 'approvalNote',
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
				</Fragment>
			) : null}

			<FormAlert alerts={alerts} />
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="marketingAspectNotes"
				initialValues={
					{
						marketingAspectNotes: marketingAspect?.marketingAspectNotes,
					} as MarketingAspect
				}
			>
				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('marketingAspectNotes')}
							label="Informasi Tambahan"
							labelCol={{
								span: 6,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -5,
								},
							}}
							rules={[rule.required('Informasi Tambahan wajib diisi')]}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 30,
				}}
				loading={submitFRMOfficeObservationMarketingAspectMutation.isPending}
				onClick={() => {
					validate();
					form.submit();
				}}
			>
				SAVE
			</Button>
		</Form.Provider>
	);
};

export default FRMVisitReportMarketingAspect;
